/* ═══ TEMA D — „hală clară" (29 sep 2026) ═══════════════════════════════════
 *
 * Dan a aprobat direcția D (combinația A+B din machetele de pe pânză): fond gri
 * de hală, suprafețe albe, bare grafit, un singur accent petrol, fontul Barlow,
 * butoane mari, culoare DOAR pentru ce nu e în regulă, pozele reale ale utilajelor.
 *
 * Regula de lansare (Dan, 29 sep): până o validează, tema o vede DOAR masterul.
 * 1 oct 2026 (Dan: „pune-o să meargă în paralel și la celelalte nivele"): comutatorul apare
 * pe TOATE conturile — fiecare om alege D sau tema de azi, pentru contul lui, pe dispozitivul
 * lui (o tabletă folosită pe schimburi nu trece alegerea unuia la ceilalți). Implicit rămâne
 * tema de azi; „pentru toată fabrica" (doar masterul) face D implicită, dar oricine poate
 * reveni la tema de azi pentru el.
 *
 * Cum e construită, ca tema de azi să rămână NESCHIMBATĂ:
 *   · `html[data-tema="d"]` aprinde secțiunea „TEMA D" de la finalul style.css;
 *   · marcajul existent primește doar clase în plus (stilurile inline rămân);
 *   · emoji-urile trec prin `sim()`, care pe tema de azi întoarce exact emoji-ul;
 *   · elementele noi (progresul zilei, bara de jos de pe telefon) există doar pe D.
 */
import { state } from './core';
import { askChoice } from './dialogs';

const CHEIE = 'fm_tema';              // VECHE (29–30 sep): alegerea pe dispozitiv, doar a masterului
const CHEIE_TOTI = 'fm_tema_toti';    // ultima valoare știută de la server (/api/config/tema)
/** Alegerea contului pe dispozitivul ăsta: 'd' | 'azi' | (nimic). */
function _cheieCont(): string { return 'fm_tema:' + (state.currentUser?.username || ''); }

function _citeste(k: string): string | null {
    try { return localStorage.getItem(k); } catch (_e) { return null; }
}
function _scrie(k: string, v: string | null): void {
    try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch (_e) { /* fără stocare */ }
}

function _alegere(): string | null {
    const a = _citeste(_cheieCont());
    if (a) return a;
    // alegerea de dinainte (pe dispozitiv) o făcea doar masterul — rămâne doar a lui
    return state.currentUser?.role === 'master' ? _citeste(CHEIE) : null;
}

/** Tema D e activă dacă omul a ales-o pentru contul lui pe dispozitivul ăsta; fără alegere,
 *  dacă masterul a pornit-o pentru toată fabrica. „Tema de azi" aleasă explicit câștigă mereu. */
export function temaD(): boolean {
    if (!state.currentUser?.role) return false;
    const alegere = _alegere();
    if (alegere === 'azi') return false;
    if (alegere === 'd') return true;
    return _citeste(CHEIE_TOTI) === '1';
}

let _fonturiIncarcate = false;
function _incarcaFonturi(): void {
    if (_fonturiIncarcate) return;
    _fonturiIncarcate = true;
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&family=Barlow+Semi+Condensed:wght@500;600;700&display=swap';
    document.head.appendChild(l);
}

/** Aplică tema după ce se știe rolul (apelată din `_applyRoleMenus`). */
export function aplicaTema(role: string): void {
    const root = document.documentElement;
    root.dataset.rol = role || 'operator';
    _verificaPentruToti();
    const btn = document.getElementById('tema-comutator');
    if (btn) {
        btn.style.display = role ? '' : 'none';
        btn.setAttribute('aria-pressed', temaD() ? 'true' : 'false');
        btn.title = temaD() ? 'Tema aplicației — poți reveni oricând la tema de azi'
            : 'Încearcă tema nouă (D · hală clară) — doar pentru contul tău, pe acest dispozitiv';
    }
    if (temaD()) {
        root.dataset.tema = 'd';
        _incarcaFonturi();
        _baraDeJos();
        _iconiteMeniu();
        _faraEmojiInTitluri();
        _pornestePlasaInline();
        _graficeD();
    } else {
        delete root.dataset.tema;
        document.getElementById('d-bara-jos')?.remove();
    }
}

/** Comutatorul (pe orice cont): tema pentru contul meu pe dispozitivul ăsta; masterul poate
 *  în plus s-o facă implicită pentru toată fabrica. */
export async function comutaTema(): Promise<void> {
    const toti = _citeste(CHEIE_TOTI) === '1';
    const acum = temaD();
    const master = state.currentUser?.role === 'master';
    const opt: Array<{ key: string; label: string; danger?: boolean }> = [
        acum ? { key: 'eu-azi', label: 'Revin la tema de azi — pentru contul meu, pe acest dispozitiv' }
             : { key: 'eu-d', label: 'Folosesc tema D — pentru contul meu, pe acest dispozitiv' },
    ];
    if (master) {
        opt.push(toti ? { key: 'toti-off', label: 'Opresc tema D ca implicită pentru toată fabrica', danger: true }
                      : { key: 'toti-on', label: 'Fac tema D implicită pentru toată fabrica' });
    }
    const ales = await askChoice('Tema aplicației',
        'Tema D (hală clară) merge în paralel cu tema de azi: fiecare alege pentru contul lui.'
        + (toti ? ' Acum D e implicită pentru toată fabrica.' : ''), opt);
    if (!ales) return;
    if (ales === 'eu-azi' || ales === 'eu-d') {
        _scrie(_cheieCont(), ales === 'eu-d' ? 'd' : 'azi');
        if (master) _scrie(CHEIE, null);      // alegerea veche, pe dispozitiv, nu mai e nevoie
    }
    if (ales === 'toti-on' || ales === 'toti-off') {
        try {
            const r = await fetch('/api/config/tema', {
                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pentru_toti: ales === 'toti-on' }),
            });
            if (!r.ok) throw new Error('HTTP ' + r.status);
            _scrie(CHEIE_TOTI, ales === 'toti-on' ? '1' : '0');
            _scrie(CHEIE, null);
            _scrie(_cheieCont(), null);           // masterul urmează implicitul pe care tocmai l-a ales
        } catch (e: any) {
            alert('Nu am putut salva alegerea pentru toată fabrica: ' + (e?.message || e));
            return;
        }
    }
    location.reload();
}

/** Valoarea „pentru toată fabrica" de la server; dacă s-a schimbat, pagina se redesenează o dată. */
let _verificatServer = false;
function _verificaPentruToti(): void {
    if (_verificatServer) return;
    _verificatServer = true;
    fetch('/api/config/tema', { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
            if (!d) return;
            const inainte = temaD();
            _scrie(CHEIE_TOTI, d.pentru_toti ? '1' : '0');
            if (temaD() !== inainte) location.reload();
        })
        .catch(() => { /* fără rețea: rămâne ultima valoare știută */ });
}

/* ── Meniul operatorului: iconițele desenate în locul emoji-urilor ──────────── */
const ICON_MENIU: Record<string, string> = {
    dashboard: 'grid', mytasks: 'wrench', journal: 'siren', parts: 'part',
    'parts-order': 'cart', alarm_history: 'bell', chat: 'chat', manuals: 'book',
    // inginer + administrare (30 sep 2026: tema pe toată aplicația)
    'schedule-intervention': 'pin', calendar: 'calendar', procedures: 'clipboard', validation: 'checkCircle',
    confirmari: 'checkSquare', diagnostic: 'search', ar: 'camera', reports: 'fileText', statistics: 'barChart',
    overview: 'factory', 'golden-set': 'flask', 'sistem-sanatate': 'heartPulse',
    '#nav-live-monitor': 'video', '#nav-users-link': 'users',
};
/** Titlurile ecranelor operatorului încep cu un emoji scris în pagină — pe D îl scoatem. */
function _faraEmojiInTitluri(): void {
    const RE = /^\s*(?:\p{Extended_Pictographic}|\p{Emoji_Presentation})\uFE0F?\s*/u;
    document.querySelectorAll('.op-section-title, .section-heading, #page-title').forEach(h => {
        const n = h.firstChild;
        if (n && n.nodeType === Node.TEXT_NODE && RE.test(n.textContent || '')) {
            n.textContent = (n.textContent || '').replace(RE, '');
        }
    });
}

function _iconiteMeniu(): void {
    document.querySelectorAll('.nav-links .nav-item').forEach(a => {
        const el = a as HTMLElement;
        const nume = ICON_MENIU[el.dataset.section || ''] || ICON_MENIU['#' + el.id];
        const loc = a.querySelector('.nav-icon');
        if (nume && loc && !loc.querySelector('.d-ic')) loc.innerHTML = ic(nume);
    });
    const grup = (id: string, nume: string) => {
        const w = document.querySelector(`#${id} .group-icon-wrap`);
        if (w && !w.querySelector('.d-ic')) w.innerHTML = ic(nume);
    };
    grup('groupEngineer', 'settings');
    grup('groupMaster', 'shield');
    // bara de sus: aceleași iconițe desenate în locul emoji-urilor
    const pune = (id: string, nume: string, text = '') => {
        const b = document.getElementById(id);
        if (b && !b.querySelector('.d-ic')) b.innerHTML = ic(nume) + (text ? `<span>${text}</span>` : '');
    };
    pune('menu-toggle', 'menu');
    pune('btn-open-2fa', 'lock');
    pune('btn-open-chpwd', 'key');
    pune('btn-spatial-demo', 'part', 'Hub 3D');
    const pa = document.querySelector('#proactive-alerts-btn .pa-icon');
    if (pa && !pa.querySelector('.d-ic')) pa.innerHTML = ic('bell');
}

/* ── Bara de meniu de jos (telefon și tabletă îngustă, doar pe D) ─────────── */
function _baraDeJos(): void {
    if (document.getElementById('d-bara-jos')) return;
    const nav = document.createElement('nav');
    nav.id = 'd-bara-jos';
    nav.setAttribute('aria-label', 'Meniu rapid');
    // Butoane, NU linkuri `#secțiune`: pe Android un link cu fragment trimite `popstate`, iar
    // paznicul butonului „înapoi" (core.ts, `_maintOnPopState`) îl lua drept „înapoi" și
    // afișa „Apasă din nou «Înapoi» pentru a părăsi aplicația" la fiecare atingere (Dan, 29 sep).
    const it = (sec: string, icon: string, et: string, badgeId = '') =>
        `<button type="button" data-dsec="${sec}">${ic(icon)}<span>${et}</span>${badgeId ? `<span class="d-bj-badge" id="${badgeId}" hidden></span>` : ''}</button>`;
    nav.innerHTML = it('dashboard', 'grid', 'Producție')
        + it('mytasks', 'wrench', 'Lucrări', 'd-bj-lucrari')
        + it('journal', 'siren', 'Urgență')
        + it('alarm_history', 'bell', 'Alarme')
        + `<button type="button" data-dsec="mai-mult">${ic('menu')}<span>Mai mult</span></button>`;
    nav.addEventListener('click', (e) => {
        const b = (e.target as HTMLElement).closest('[data-dsec]') as HTMLElement | null;
        if (!b) return;
        const sec = b.dataset.dsec || '';
        if (sec === 'mai-mult') {
            (document.getElementById('menu-toggle') as HTMLElement | null)?.click();
            return;
        }
        // același drum ca meniul lateral (rutare, titlu, încărcare), fără navigare pe adresă
        (document.querySelector(`.nav-links a[data-section="${sec}"]`) as HTMLElement | null)?.click();
    });
    document.body.appendChild(nav);
    // Ecranul activ se citește din `.section.active`, nu din `#hash`: aplicația schimbă
    // adresa cu `history.replaceState`, care NU trimite `hashchange` (29 sep 2026 — bara
    // arăta „Urgență" aprinsă în timp ce Dan era în Lucrările mele).
    const marcheaza = () => {
        const act = document.querySelector('.section.active') as HTMLElement | null;
        const h = act?.id?.replace(/^sec-/, '') || (location.hash || '#dashboard').slice(1);
        nav.querySelectorAll('[data-dsec]:not([data-dsec="mai-mult"])').forEach(a => {
            const on = (a as HTMLElement).dataset.dsec === h;
            a.classList.toggle('on', on);
            if (on) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
        });
    };
    window.addEventListener('hashchange', marcheaza);
    const obs = new MutationObserver(marcheaza);
    document.querySelectorAll('.section').forEach(sec => obs.observe(sec, { attributes: true, attributeFilter: ['class'] }));
    marcheaza();
}

/* ── Emoji → iconițe desenate, în interfață (30 sep 2026: tema pe toată aplicația) ──────
 * Doar emoji-ul de la ÎNCEPUTUL unui text (poziția de iconiță din butoane, titluri, file,
 * etichete) și doar cele din tabelul de mai jos; restul rămân cum sunt. Textele oamenilor și
 * ale AI-ului (chat, răspunsuri, câmpuri, cod) nu se ating. Structura se schimbă minim: un
 * `<span class="d-em">` înaintea textului (un singur cod rescrie primul nod — tasks-calendar
 * — și pe D nu mai scrie emoji). */
const EMOJI_IC: Record<string, string> = {
    '🔧': 'wrench', '🛠': 'wrench', '🔩': 'part', '⚙': 'settings', '📋': 'clipboard', '📝': 'edit', '✏': 'edit', '🖊': 'edit',
    '🗑': 'trash', '✕': 'x', '✖': 'x', '❌': 'x', '✓': 'check', '✔': 'check', '✅': 'checkCircle', '☑': 'checkSquare',
    '⚠': 'warn', '🚨': 'siren', '🔔': 'bell', '🔕': 'bell', '📅': 'calendar', '📆': 'calendar', '🗓': 'calendar',
    '🤖': 'chat', '💬': 'chat', '📖': 'book', '📚': 'book', '📘': 'book', '📕': 'book', '🔍': 'search', '🔎': 'search',
    '📷': 'camera', '📸': 'camera', '📊': 'barChart', '📈': 'barChart', '📉': 'barChart', '📑': 'fileText', '📄': 'fileText',
    '📃': 'fileText', '🏭': 'factory', '⚡': 'bolt', '🔋': 'bolt', '🕐': 'clock', '⏱': 'clock', '⏰': 'clock', '⌛': 'clock',
    '⏳': 'clock', '🕒': 'clock', '⏸': 'pause', '▶': 'play', '⏯': 'play', '⏹': 'stop', '🛒': 'cart', '👁': 'eye', '👀': 'eye',
    '📍': 'pin', '📌': 'pin', '❄': 'snow', '🌡': 'thermo', '💧': 'drop', '🛢': 'drop', '💾': 'save', '📤': 'upload',
    '📥': 'download', '⬇': 'download', '⬆': 'upload', '🔄': 'refresh', '↻': 'refresh', '🔃': 'refresh', '⟳': 'refresh',
    '➕': 'plus', '👤': 'user', '👥': 'users', '🔐': 'lock', '🔒': 'lock', '🔑': 'key', '🧪': 'flask', '🩺': 'heartPulse',
    '🗺': 'map', '🎥': 'video', '📹': 'video', '⭐': 'star', '★': 'star', 'ℹ': 'info', '❓': 'help', '❔': 'help',
    '💡': 'bulb', '📦': 'box', '🧩': 'mold', '🔲': 'mold', '🖨': 'printer', '🌀': 'wind', '💨': 'wind', '🔥': 'flame',
    '🖥': 'monitor', '💻': 'monitor', '📡': 'signal', '🔗': 'link', '📎': 'paperclip', '↩': 'undo', '←': 'back',
    '📤︎': 'upload', '🚀': 'send', '📨': 'send', '🎤': 'mic', '🖼': 'image', '🌐': 'globe', '📐': 'ruler', '📏': 'ruler',
    '🔽': 'filter', '🎯': 'target', '🏷': 'tag', '📱': 'phone', '✉': 'mail', '📧': 'mail', '🏠': 'home', '🧠': 'brain',
    '♻': 'refresh', '🧹': 'trash', '⛔': 'octagon', '🛑': 'octagon', '🚫': 'octagon', '🔴': '•rosu', '🟢': '•verde',
    '🟡': '•galben', '🟠': '•galben', '🔵': '•albastru', '⚪': '•gri', '⚫': '•gri', '🟣': '•albastru',
    // văzute de turul temei (30 sep 2026): ora din tabele, cărțile, alarmele fără sunet, săgețile
    '🕓': 'clock', '🕑': 'clock', '🕔': 'clock', '⏲': 'clock', '📗': 'book', '📒': 'book', '📓': 'book', '🎓': 'book',
    '🔇': 'bellOff', '🔕︎': 'bellOff', '▾': 'chevD', '▼': 'chevD', '▸': 'chev', '▴': 'chevU', '↺': 'undo', '→': 'chev',
    '🔢': 'hash', '🔌': 'plug', '📁': 'folder', '📂': 'folder', '⏻': 'power', '🧑‍🔧': 'user', '👷': 'user',
    '📜': 'fileText', '🧊': 'snow', '🔬': 'search', '🏗': 'factory', '⛶': 'maximize', '☁': 'cloud', '✎': 'edit',
    '💳': 'tag',
};
const RE_EM = /^(\s*)((?:\p{Extended_Pictographic}|[\u2190-\u21FF\u2300-\u23FF\u25A0-\u27BF\u2B00-\u2BFF\u2139])\uFE0F?(?:\u200D\p{Extended_Pictographic}\uFE0F?)*)\s*/u;
const _EM_NU = '.chat-messages, .chat-msg-content, .pa-msg, .help-req-msg, .help-req-response, .gs-q-ai-answer, '
    + 'textarea, input, select, option, [contenteditable], pre, code, script, style, svg, canvas, #d-bara-jos, [data-d-pastreaza]';
export const emojiNecunoscute = new Map<string, number>();
(window as any).__dEmojiNecunoscute = emojiNecunoscute;   // pentru proba temei (ce emoji n-au încă iconiță)
function _emojiInText(n: Text): void {
    const m = RE_EM.exec(n.data);
    if (!m) return;
    const p = n.parentElement;
    if (!p || p.closest(_EM_NU)) return;
    const em = m[2].replace(/\uFE0F/g, '');
    const tinta = EMOJI_IC[em] || EMOJI_IC[m[2]];
    if (!tinta) { emojiNecunoscute.set(em, (emojiNecunoscute.get(em) || 0) + 1); return; }
    const span = document.createElement('span');
    span.className = 'd-em';
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML = tinta.startsWith('•') ? `<i class="d-bulina d-b-${tinta.slice(1)}"></i>` : ic(tinta);
    p.insertBefore(span, n);
    n.data = n.data.slice(m[0].length);
    if (n.data) span.classList.add('d-em-text');
}
function _emojiSub(root: Node): void {
    if (root.nodeType === 3) { _emojiInText(root as Text); return; }
    if (root.nodeType !== 1) return;
    const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const lista: Text[] = [];
    let t: Node | null;
    while ((t = tw.nextNode())) { if (RE_EM.test((t as Text).data)) lista.push(t as Text); }
    lista.forEach(_emojiInText);
}

/** Cifra de pe „Lucrări" din bara de jos (aceeași ca insigna din meniul lateral). */
export function dBaraJosLucrari(n: number): void {
    const b = document.getElementById('d-bj-lucrari');
    if (!b) return;
    b.hidden = !n;
    b.textContent = String(n);
}

/* ── Plasa pentru stilurile scrise direct în cod (inline), doar pe tema D ─────
 * Aceeași regulă ca `scripts/tema_d_plasa.py` (care o aplică pe style.css), dar pentru
 * `style="…"` din ecranele desenate de TS, pe care CSS-ul nu le poate atinge fără clase.
 * Fundal închis aproape gri → suprafață; text deschis pe element fără fundal colorat →
 * text închis sau culoarea de stare; strălucirile → fără. Tema de azi nu trece pe aici. */
type Rgba = [number, number, number, number];
const RE_CUL = /#[0-9a-fA-F]{3,8}\b|rgba?\([^)]*\)/g;
function _culoriDin(v: string): Rgba[] {
    const out: Rgba[] = [];
    (v.match(RE_CUL) || []).forEach(t => {
        if (t[0] === '#') {
            let h = t.slice(1);
            if (h.length === 3 || h.length === 4) h = h.split('').map(c => c + c).join('');
            if (h.length !== 6 && h.length !== 8) return;
            out.push([parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16),
                h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1]);
        } else {
            const n = (t.match(/[\d.]+%?/g) || []).map(x => x.endsWith('%') ? parseFloat(x) * 2.55 : parseFloat(x));
            if (n.length >= 3) out.push([n[0], n[1], n[2], n.length >= 4 ? (t.match(/[\d.]+%?/g)![3].endsWith('%') ? n[3] / 255 : n[3]) : 1]);
        }
    });
    return out;
}
function _lumD(c: Rgba): number {
    const f = (x: number) => { x /= 255; return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4); };
    return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]);
}
const _croma = (c: Rgba) => Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2]);
function _textD(c: Rgba): string {
    if (_croma(c) < 40) return _lumD(c) > 0.7 && c[3] >= 0.85 ? 'var(--text)' : 'var(--text-muted)';
    const r = c[0] / 255, g = c[1] / 255, b = c[2] / 255, mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
    let h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
    if (h < 20 || h >= 330) return 'var(--d-bad-txt)';
    if (h < 70) return 'var(--d-warn-txt)';
    if (h < 170) return 'var(--d-ok-txt)';
    return 'var(--d-accent-text)';
}
/** Vălul semitransparent de sub o fereastră (umbrește pagina) — rămâne închis. */
function _eVal(el: HTMLElement): boolean {
    if (/overlay|backdrop|scrim|dimmer|lightbox/i.test(el.className || '') || /overlay|backdrop/i.test(el.id || '')) return true;
    const st = el.style;
    return st.position === 'fixed' && (st.inset === '0px' || st.inset === '0' || (st.top === '0px' && st.left === '0px'));
}
/** Nuanța deschisă pentru un fundal aproape negru, dar colorat (bannere maro / vișinii). */
function _nuantaSub(c: Rgba): string {
    const t = _textD(c);
    return t === 'var(--d-bad-txt)' ? 'var(--d-bad-sub)' : t === 'var(--d-warn-txt)' ? 'var(--d-warn-sub)'
        : t === 'var(--d-ok-txt)' ? 'var(--d-ok-sub)' : 'var(--d-accent-subtle)';
}
/** Culoarea plină a temei pentru un degradeu colorat (aceeași regulă ca în scripts/tema_d_plasa.py). */
function _plinD(c: Rgba): string {
    const r = c[0] / 255, g = c[1] / 255, b = c[2] / 255, mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
    let h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
    if (h < 15 || h >= 330) return 'var(--d-bad)';
    if (h < 45) return '#B54708';
    if (h < 70) return 'var(--d-warn-ic)';
    if (h < 170) return 'var(--d-ok)';
    return 'var(--d-accent)';
}
/** 'inchis' | 'sub:<culoare>' | 'colorat' | '' pentru fundalul inline al unui element. */
function _felFundal(el: HTMLElement): string {
    const v = el.style.background || el.style.backgroundColor || '';
    if (!v) return '';
    if (/var\(--(?!surface|panel|card-bg|bg|bg-card|border)/.test(v)) return 'colorat';
    // degradeu care se stinge în transparent = legendă peste o fotografie: rămâne închisă, cu text deschis
    if (/gradient/i.test(v) && /transparent|rgba\([^)]*,\s*0\)/i.test(v) && _culoriDin(v).every(c => c[3] < 0.85)) return 'colorat';
    // degradeu colorat (buton „strălucitor") → culoarea plină a temei, după nuanța primei culori
    if (/gradient/i.test(v)) {
        const sat = _culoriDin(v).filter(c => c[3] >= 0.5 && _croma(c) >= 60);
        if (sat.length) return 'plin:' + _plinD(sat[0]);
    }
    const toate = _culoriDin(v);
    const opace = toate.filter(c => c[3] >= 0.5);
    const cs = opace.length ? opace : toate.filter(c => c[3] >= 0.15);
    if (!cs.length) return '';
    // Opacitatea nu mai decide singură (30 sep, ca în scripts/tema_d_plasa.py): vălul se
    // recunoaște după formă; orice alt panou gri-închis, chiar semitransparent, devine deschis.
    if (_eVal(el)) return '';
    if (cs.every(c => _lumD(c) < 0.12 && _croma(c) < 60)) return cs.some(c => c[3] >= 0.5) ? 'inchis' : 'adancit';
    if (cs.every(c => _lumD(c) < 0.05 && _croma(c) >= 60 && c[3] >= 0.85)) return 'sub:' + _nuantaSub(cs[0]);
    return cs.some(c => c[3] >= 0.5) ? 'colorat' : '';
}
const _NU_ATINGE = '#sidebar, #d-bara-jos, .manual-overlay, .wf-pdf-panel, canvas, video, .lightbox, [data-d-pastreaza]';
function _plasaPeElement(el: HTMLElement): void {
    if (el.closest(_NU_ATINGE)) return;
    const st = el.style;
    const fel = _felFundal(el);
    if (fel.startsWith('plin:')) { st.background = fel.slice(5); return; }
    // etichetă colorată DESCHIS cu text alb (ex. „Validat" alb pe verde #22c55e, 2,3:1) → culoarea plină,
    // închisă, a aceleiași nuanțe (verdele temei: 5,3:1)
    if (fel === 'colorat' && st.color) {
        const t = _culoriDin(st.color)[0];
        const f = _culoriDin(st.background || st.backgroundColor || '').find(c => c[3] >= 0.85 && _croma(c) >= 60);
        if (t && f && _lumD(t) > 0.8 && _lumD(f) > 0.2) st.background = _plinD(f);
        // buton NEON cu text negru (verde #00e676, galben #f59e0b) → culoarea plină a temei, text alb
        else if (t && f && _lumD(t) < 0.1 && _lumD(f) > 0.3) { st.background = _plinD(f); st.color = '#fff'; }
    }
    if (fel === 'inchis' || fel === 'adancit' || fel.startsWith('sub:')) {
        st.background = fel === 'inchis' ? 'var(--surface)' : fel === 'adancit' ? 'var(--d-sunken)' : fel.slice(4);
        if (/#[0-9a-f]{3,8}|rgba?\(/i.test(st.borderColor || st.border || '')) st.borderColor = 'var(--border)';
    }
    if (st.color && fel !== 'colorat') {
        const p1 = el.parentElement, p2 = p1?.parentElement;
        const colorat = (e: HTMLElement | null | undefined) => { const f = e ? _felFundal(e) : ''; return f === 'colorat' || f.startsWith('plin:'); };
        const parinteColorat = colorat(p1) || colorat(p2);
        const c = _culoriDin(st.color)[0];
        if (c && !parinteColorat && c[3] >= 0.45 && _lumD(c) > 0.15) st.color = _textD(c);
    }
    if (st.textShadow && st.textShadow !== 'none') st.textShadow = 'none';
    // fonturile fără ș/ț și majusculele răsfirate — aceeași regulă ca în scripts/tema_d_plasa.py
    const ff = st.fontFamily || '';
    if (/orbitron|rajdhani/i.test(ff)) st.fontFamily = 'var(--sans)';
    else if (/share tech|jetbrains|courier|monospace|--mono/i.test(ff) && !el.closest('pre, code')) st.fontFamily = 'var(--d-num)';
    if (st.textTransform === 'uppercase' && st.letterSpacing && !/^(0|0px|0em|0rem|normal)$/.test(st.letterSpacing)) {
        st.textTransform = 'none'; st.letterSpacing = '0';
    }
}
function _plasaSub(root: ParentNode): void {
    if (root instanceof HTMLElement && root.hasAttribute('style')) _plasaPeElement(root);
    root.querySelectorAll?.('[style]').forEach(e => _plasaPeElement(e as HTMLElement));
}
let _plasaPornita = false;
function _pornestePlasaInline(): void {
    if (_plasaPornita) return;
    _plasaPornita = true;
    _plasaSub(document.body);
    _emojiSub(document.body);
    // și schimbările de stil de după apariție (ex. butoanele 2D / 3D își rescriu culorile la comutare);
    // plasa e idempotentă: ce a scris ea (`var(--…)`) nu mai declanșează nimic la trecerea următoare
    new MutationObserver(muts => {
        for (const m of muts) {
            if (m.type === 'attributes') { if (m.target.nodeType === 1) _plasaPeElement(m.target as HTMLElement); continue; }
            if (m.type === 'characterData') { _emojiSub(m.target); continue; }
            m.addedNodes.forEach(n => { if (n.nodeType === 1) _plasaSub(n as HTMLElement); _emojiSub(n); });
        }
    }).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style'] });
}

/* ── Graficele (Chart.js) pe paleta temei D ───────────────────────────────────
 * Graficele de azi au culori neon alese pentru fond negru (cyan #00c8ff, verde #00e676…); pe alb
 * sunt stridente, iar textul axelor era deschis. Un singur plugin, înainte de fiecare desenare:
 * fiecare culoare de serie → culoarea temei cu aceeași nuanță (transparența se păstrează), iar
 * textul, grila și fontul vin din temă. Graficele nu se schimbă altfel (date, tip, ordine). */
const PALETA_D: Array<[number, string]> = [
    [15, '#B42318'], [45, '#C4570A'], [70, '#B58100'], [170, '#0B7A55'], [200, '#0E7C99'], [250, '#1D5FBF'], [330, '#6E56CF'], [361, '#B42318'],
];
function _culoareD(v: unknown): unknown {
    if (typeof v !== 'string') return v;
    const c = _culoriDin(v)[0];
    if (!c) return v;
    const a = c[3];
    let hex: string;
    if (_croma(c) < 40) hex = _lumD(c) > 0.6 ? '#C9D0D6' : '#8A949E';
    else {
        const r = c[0] / 255, g = c[1] / 255, b = c[2] / 255, mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
        let h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
        h = (h * 60 + 360) % 360;
        hex = (PALETA_D.find(([lim]) => h < lim) || PALETA_D[0])[1];
    }
    if (a >= 0.99) return hex;
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}
const _mapCulori = (v: unknown) => Array.isArray(v) ? v.map(_culoareD) : _culoareD(v);
let _graficePornite = false;
function _graficeD(): void {
    const Ch = (window as any).Chart;
    if (!Ch || _graficePornite) return;
    _graficePornite = true;
    try {
        Ch.defaults.color = '#37414B';
        Ch.defaults.borderColor = '#DDE2E7';
        Ch.defaults.font.family = "'Barlow', system-ui, sans-serif";
        Ch.register({
            id: 'temaD',
            beforeUpdate(chart: any) {
                const serii = chart.data?.datasets || [];
                const tip = chart.config?.type;
                serii.forEach((ds: any) => {
                    if (ds._temaD) return;
                    ds._temaD = true;
                    // o singură serie de bare cu câte o culoare pe bară = „curcubeu" fără înțeles → o culoare
                    if (serii.length === 1 && (tip === 'bar' || ds.type === 'bar') && Array.isArray(ds.backgroundColor)
                        && new Set(ds.backgroundColor).size > 2) {
                        ds.backgroundColor = '#0B6480';
                        ds.borderColor = '#0B6480';
                        return;
                    }
                    ['backgroundColor', 'borderColor', 'pointBackgroundColor', 'pointBorderColor', 'hoverBackgroundColor'].forEach(k => {
                        if (ds[k] != null && typeof ds[k] !== 'function') ds[k] = _mapCulori(ds[k]);
                    });
                });
                const sc = chart.options?.scales || {};
                Object.keys(sc).forEach(k => {
                    const s = sc[k];
                    if (s?.ticks) s.ticks.color = '#37414B';
                    if (s?.title) s.title.color = '#37414B';
                    if (s?.grid) s.grid.color = '#E1E5EA';
                });
                const lg = chart.options?.plugins?.legend?.labels;
                if (lg) lg.color = '#37414B';
                const ti = chart.options?.plugins?.title;
                if (ti) ti.color = '#0F151B';
            },
        });
    } catch (_e) { /* fără Chart.js pe pagină — nimic de făcut */ }
}

/* ── Iconițe: un singur set, desenate cu linie (stil Lucide, licență ISC) ──── */
export const IC: Record<string, string> = {
    grid: 'M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z',
    wrench: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
    siren: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z M12 9v4 M12 17h.01',
    warn: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z M12 9v4 M12 17h.01',
    bell: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9 M10.3 21a1.9 1.9 0 0 0 3.4 0',
    menu: 'M4 6h16 M4 12h16 M4 18h16',
    pause: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M10 15V9 M14 15V9',
    play: 'M6 4l13 8-13 8z',
    check: 'M20 6 9 17l-5-5',
    checkCircle: 'M22 11.1V12a10 10 0 1 1-5.9-9.1 M22 4 12 14l-3-3',
    x: 'M18 6 6 18 M6 6l12 12',
    clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 6v6l4 2',
    octagon: 'M7.9 2h8.2L22 7.9v8.2L16.1 22H7.9L2 16.1V7.9z M12 8v4 M12 16h.01',
    info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 16v-4 M12 8h.01',
    eye: 'M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    drop: 'M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5S12.5 5.5 12 3c-.5 2.5-2 4.9-4 6.5S5 13 5 15a7 7 0 0 0 7 7z',
    swap: 'M16 3h5v5 M4 20 21 3 M21 16v5h-5 M15 15l6 6 M4 4l5 5',
    part: 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z M3.3 7 12 12l8.7-5 M12 22V12',
    book: 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z',
    history: 'M3 12a9 9 0 1 0 9-9 9.8 9.8 0 0 0-6.7 2.7L3 8 M3 3v5h5 M12 7v5l4 2',
    handover: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M17 11l2 2 4-4',
    calendar: 'M8 2v4 M16 2v4 M3 10h18 M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
    snow: 'M12 2v20 M4.9 4.9l14.2 14.2 M2 12h20 M4.9 19.1 19.1 4.9',
    more: 'M12 12h.01 M19 12h.01 M5 12h.01',
    search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z M21 21l-4.3-4.3',
    refresh: 'M21 12a9 9 0 0 0-9-9 9.8 9.8 0 0 0-6.7 2.7L3 8 M3 3v5h5 M3 12a9 9 0 0 0 9 9 9.8 9.8 0 0 0 6.7-2.7L21 16 M16 16h5v5',
    plus: 'M12 5v14 M5 12h14',
    user: 'M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
    camera: 'M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z M12 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    run: 'M22 12h-4l-3 9L9 3l-3 9H2',
    stop: 'M6 6h12v12H6z',
    video: 'M16 13l5.2 3.5a.5.5 0 0 0 .8-.4V7.9a.5.5 0 0 0-.8-.4L16 11 M4 6h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z',
    cart: 'M3 3h2l2.7 12.4a2 2 0 0 0 2 1.6h9.4a2 2 0 0 0 2-1.6L22.7 7H6 M9 21h.01 M18 21h.01',
    chat: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
    chev: 'M9 18l6-6-6-6',
    chevL: 'M15 18l-6-6 6-6',
    chevD: 'M6 9l6 6 6-6',
    chevU: 'M18 15l-6-6-6 6',
    bellOff: 'M8.7 3A6 6 0 0 1 18 8a21.3 21.3 0 0 0 .6 5 M17 17H3s3-2 3-9a4.7 4.7 0 0 1 .3-1.7 M10.3 21a1.9 1.9 0 0 0 3.4 0 M2 2l20 20',
    hash: 'M4 9h16 M4 15h16 M10 3 8 21 M16 3l-2 18',
    plug: 'M12 22v-5 M9 8V2 M15 8V2 M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8z',
    folder: 'M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.7-.9l-.8-1.2A2 2 0 0 0 7.9 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2z',
    power: 'M12 2v10 M18.4 6.6a9 9 0 1 1-12.8 0',
    maximize: 'M8 3H5a2 2 0 0 0-2 2v3 M21 8V5a2 2 0 0 0-2-2h-3 M3 16v3a2 2 0 0 0 2 2h3 M16 21h3a2 2 0 0 0 2-2v-3',
    cloud: 'M17.5 19H9a7 7 0 1 1 6.7-9h1.8a4.5 4.5 0 1 1 0 9z',
    minus: 'M5 12h14',
    external: 'M15 3h6v6 M10 14 21 3 M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6',
    factory: 'M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z M17 18h1 M12 18h1 M7 18h1',
    mold: 'M12 2 2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5',
    wind: 'M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2 M9.6 4.6A2 2 0 1 1 11 8H2 M12.6 19.4A2 2 0 1 0 14 16H2',
    flame: 'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.4-.5-2-1-3-1.1-2.1-.2-4 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.2.4-2.3 1-3a2.5 2.5 0 0 0 2.5 2.5z',
    monitor: 'M4 3h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M8 21h8 M12 17v4',
    printer: 'M6 9V2h12v7 M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2 M6 14h12v8H6z',
    robot: 'M12 8V4H8 M6 8h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2z M2 14h2 M20 14h2 M15 13v2 M9 13v2',
    map: 'M14.1 5.9 9 3 3 6v15l6-3 6 3 6-3V3z M9 3v15 M15 6v15',
    pin: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    clipboard: 'M9 2h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2 M9 12h6 M9 16h6',
    checkSquare: 'M9 11l3 3L22 4 M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
    fileText: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8',
    barChart: 'M3 3v18h18 M18 17V9 M13 17V5 M8 17v-3',
    users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M22 21v-2a4 4 0 0 0-3-3.9 M16 3.1a4 4 0 0 1 0 7.8',
    flask: 'M9 3h6 M10 3v6.5L4.5 19a2 2 0 0 0 1.7 3h11.6a2 2 0 0 0 1.7-3L14 9.5V3 M7 15h10',
    heartPulse: 'M19 14c1.5-1.5 3-3.2 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.8 0-3 .5-4.5 2-1.5-1.5-2.7-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4 3 5.5l7 7z M3.2 12H9l.5-1 2 4.5 2-7 1.5 3.5h5.3',
    settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
    shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    lock: 'M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z M7 11V7a5 5 0 0 1 10 0v4',
    key: 'M15.5 7.5 19 4 M21 2l-2 2 M11.4 11.6a5.5 5.5 0 1 1-7.8 7.8 5.5 5.5 0 0 1 7.8-7.8z M11.4 11.6 15.5 7.5l3 3L22 7l-3-3',
    edit: 'M12 20h9 M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
    trash: 'M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6 M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M10 11v6 M14 11v6',
    bolt: 'M13 2 3 14h9l-1 8 10-12h-9l1-8z',
    thermo: 'M14 14.8V4a2 2 0 0 0-4 0v10.8a4 4 0 1 0 4 0z',
    save: 'M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z M17 21v-8H7v8 M7 3v5h8',
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    star: 'M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z',
    help: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3 M12 17h.01',
    bulb: 'M9 18h6 M10 22h4 M15.1 14c.2-1 .7-1.7 1.4-2.5A4.6 4.6 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5',
    box: 'M21 8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1-1.7z M3.3 7 12 12l8.7-5 M12 22V12',
    signal: 'M2 20h.01 M7 20v-4 M12 20v-8 M17 20V8 M22 4v16',
    link: 'M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7 M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7',
    paperclip: 'M21.4 11.1l-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5',
    undo: 'M9 14 4 9l5-5 M4 9h10.5a5.5 5.5 0 0 1 0 11H11',
    back: 'M19 12H5 M12 19l-7-7 7-7',
    send: 'M22 2 11 13 M22 2l-7 20-4-9-9-4z',
    mic: 'M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z M19 10v2a7 7 0 0 1-14 0v-2 M12 19v3',
    image: 'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M21 15l-5-5L5 21',
    globe: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M2 12h20 M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z',
    ruler: 'M21.3 15.3 8.7 2.7a1 1 0 0 0-1.4 0L2.7 7.3a1 1 0 0 0 0 1.4l12.6 12.6a1 1 0 0 0 1.4 0l4.6-4.6a1 1 0 0 0 0-1.4z M7.5 10.5l2-2 M10.5 13.5l2-2 M13.5 16.5l2-2',
    filter: 'M22 3H2l8 9.5V19l4 2v-8.5z',
    lightning: 'M13 2 3 14h9l-1 8 10-12h-9l1-8z',
    target: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12z M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    tag: 'M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z M7 7h.01',
    phone: 'M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z M12 18h.01',
    mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z M22 6l-10 7L2 6',
    home: 'M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
    brain: 'M12 5a3 3 0 1 0-6 .1 4 4 0 0 0-2.5 5.8 4 4 0 0 0 .5 6.6A4 4 0 0 0 12 18z M12 5a3 3 0 1 1 6 .1 4 4 0 0 1 2.5 5.8 4 4 0 0 1-.5 6.6A4 4 0 0 1 12 18z',
};

/** SVG inline pentru o iconiță din set (moștenește culoarea textului). */
export function ic(name: string, cls = ''): string {
    const d = IC[name] || IC.info;
    return `<svg class="d-ic${cls ? ' ' + cls : ''}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${d}"></path></svg>`;
}

/** Pe tema D: iconița desenată; pe tema de azi: exact emoji-ul de până acum. */
export function sim(name: string, emoji: string): string {
    return temaD() ? ic(name) : emoji;
}
